import { useState, useEffect, useCallback } from "react";
import { COLUMNS } from "@/lib/table-columns";
import type { VisibilityState } from "./types";

const VISIBILITY_STORAGE_KEY = "domains-table-visibility";
const ORDER_STORAGE_KEY = "domains-table-column-order";

export const useColumnVisibility = () => {
  const [visibleColumns, setVisibleColumns] = useState<VisibilityState>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem(VISIBILITY_STORAGE_KEY);
        if (saved) {
          return JSON.parse(saved);
        }
      } catch (e) {
        console.error("Failed to parse column visibility", e);
      }
    }
    const initial: VisibilityState = {};
    COLUMNS.forEach((col) => {
      initial[col.key] = true;
    });
    return initial;
  });

  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const defaultOrder = COLUMNS.map((c) => c.key);

    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem(ORDER_STORAGE_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          
          if (parsed.length !== defaultOrder.length) {
             return defaultOrder;
          }
          
          return parsed;
        }
      } catch (e) {
        console.error("Failed to parse column order", e);
      }
    }
    return defaultOrder;
  });

  useEffect(() => {
    localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(columnOrder));
  }, [columnOrder]);

  const toggleColumn = useCallback((key: string, isVisible: boolean) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [key]: isVisible,
    }));
  }, []);

  const reorderColumns = useCallback((newOrder: string[]) => {
    setColumnOrder(newOrder);
  }, []);

  return {
    visibleColumns,
    columnOrder,
    toggleColumn,
    reorderColumns,
  };
};
